import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { AppState, LanguageState } from 'acscp/store';
import { AddSharingToken, ChangeLocaleFromSharingLink, ContinueAsGuest, EnterNonJetPortalMode, RemoveSharingTokens } from 'acscp/actions';
import { LoadingIndicator } from 'acscp/components/common/loadingIndicator';
import { Redirect, Route, RouteProps } from 'react-router';
import { userIsLoggedIn } from 'acscp/store/helpers/authHelper';
import { getValidLocale, LocaleCode } from 'acscp/translations';
import { loginAndRedirectTo } from './routeHelper';

interface SharingTokenAwareRouteProps<P> extends RouteProps {
    component: React.ComponentType<P>;
}

interface TokenHandlingStateProps {
    sharingTokens: string[];
    isLoggedIn: boolean;
    languageState: LanguageState;
}

interface TokenHandlingDispatchProps {
    addSharingToken: (token: string) => void;
    removeSharingToken: (token: string) => void;
    changeLocaleFromSharingLink: (locale: LocaleCode) => void;
    loginAsGuest: () => void;
    enterNonJetPortalMode: () => void;
}

type TokenHandlingComponentProps<P> = P & TokenHandlingDispatchProps & TokenHandlingStateProps;

interface TokenHandlingComponentState {
    showLoadingIndicator: boolean;
}

export function SharingTokenAwareRoute<P>(
    {component: Component, ...rest}: SharingTokenAwareRouteProps<P>
): JSX.Element {

    class UnconnectedComponent
        extends React.Component<TokenHandlingComponentProps<P>, TokenHandlingComponentState> {

        constructor(props: TokenHandlingComponentProps<P>) {
            super(props);
            this.state = { showLoadingIndicator: false };
        }

        private get sharingToken(): string | null {
            const params = new URLSearchParams(rest.location!.search);
            return params.get('t') !== null ? params.get('t') : params.get('sharingToken');
        }

        private get defaultLanguage(): string | null {
            const params = new URLSearchParams(rest.location!.search);
            return params.get('l') !== null ? params.get('l') : params.get('defaultLanguage');
        }

        private get continueAsGuest(): boolean {
            const params = new URLSearchParams(rest.location!.search);
            return params.has('g') || params.get('continueAsGuest') === 'true';
        }

        private get isNonJetPortalUser(): boolean {
            return new URLSearchParams(rest.location!.search).has('n');
        }

        componentWillMount(): void {
            if (this.defaultLanguage && this.props.languageState <= LanguageState.SetBySharingLink) {
                this.props.changeLocaleFromSharingLink(getValidLocale(this.defaultLanguage));
            }
            if (this.sharingToken &&
                !this.props.sharingTokens.find(token => token === this.sharingToken)) {

                this.props.addSharingToken(this.sharingToken);

                // We need to give time to the Redux to update the store and
                // the Redux saga handler to "notice" the changes.

                // If we don't allow this update to happen,
                // then we will try to perform some requests without having sufficient authentication information.
                this.setState({ showLoadingIndicator: true });
            }
            if (this.continueAsGuest && !this.props.isLoggedIn) {
                this.props.loginAsGuest();
            }
            if (this.isNonJetPortalUser && !this.props.isLoggedIn) {
                this.props.enterNonJetPortalMode();
            }
        }

        componentWillReceiveProps(nextProps: Readonly<TokenHandlingComponentProps<P>>): void {
            if (this.state.showLoadingIndicator) {
                this.setState({ showLoadingIndicator: false });
            }
        }

        shouldComponentUpdate(
            nextProps: Readonly<TokenHandlingComponentProps<P>>,
            nextState: TokenHandlingComponentState
        ): boolean {

            // We only need to re-render this component if we need to remove the LoadingIndicator.
            // Changes to other props should not affect it:
            // - Most of the time we don't care about changes to sharing tokens.
            //   We only change to them we track is addition of the new token
            //   when we need to stop showing the LoadingIndicator
            // - Dispatch props should never change
            // - Wrapped component props don't affect rendering of this component
            return nextState.showLoadingIndicator !== this.state.showLoadingIndicator;
        }

        render(): JSX.Element {
            if (!this.props.isLoggedIn && !this.isNonJetPortalUser) {
                return <Redirect to={loginAndRedirectTo(rest.location!)} />;
            }
            if (this.state.showLoadingIndicator) {
                return <LoadingIndicator />;
            }

            return <Component {...this.props} />;
        }
    }

    const mapStateToProps = (state: AppState, ownProps: P) => ({
        sharingTokens: state.auth.sharingTokens,
        isLoggedIn: state.auth.guestLogin || userIsLoggedIn(state.auth),
        languageState: state.intl.languageState
    });

    function mapDispatchToProps(dispatch: Dispatch, ownProps: P): TokenHandlingDispatchProps {
        return {
            addSharingToken: (token: string) => dispatch(new AddSharingToken(token)),
            removeSharingToken: (token: string) => dispatch(new RemoveSharingTokens([token])),
            changeLocaleFromSharingLink: (locale: LocaleCode) => dispatch(new ChangeLocaleFromSharingLink(locale)),
            loginAsGuest: () => dispatch(new ContinueAsGuest()),
            enterNonJetPortalMode: () => dispatch(new EnterNonJetPortalMode())
        };
    }

    // FIXME: we used "any" here to silence the typing warnings,
    // but supposedly we should be able to resolve them by adding some constraints on the type variable P.
    const TokenHandlingComponent = connect(mapStateToProps, mapDispatchToProps)(UnconnectedComponent as any);

    return <Route {...rest} component={TokenHandlingComponent} />;
}
